/**
 * The language the signed-in child is learning
 * ([D-189](../../../docs/DECISIONS.md#d-189)).
 *
 * The app itself always talks English ([D-068](../../../docs/DECISIONS.md#d-068));
 * what changes with the course is the language of the *words being taught*:
 * which voice reads them, which language the microphone listens for, and the
 * `lang` a screen reader is told they are in.
 *
 * A module value rather than React state because the readers are plain
 * functions — the voice picker, the recogniser — called from deep inside
 * exercises. The app sets it when it learns the child's course and when the
 * child switches; until then it is English, which is what every child had
 * before there was a choice.
 */

/** One course, as the app needs to know it. */
export interface Learning {
  readonly courseId: string;
  /** ISO 639-1: `en`, `sv`. */
  readonly l2: string;
  /** What a child calls it: `English`, `Swedish`. */
  readonly title: string;
}

export const ENGLISH: Learning = { courseId: 'en.da', l2: 'en', title: 'English' };

let learning: Learning = ENGLISH;

/** The course being learned now. */
export const learningNow = (): Learning => learning;

/** Switch it. Null goes back to English, as at sign-out. */
export function setLearning(next: Learning | null): void {
  learning = next ?? ENGLISH;
}

/**
 * The tag a speech recogniser is asked to listen for.
 *
 * English keeps `en-US`, which is what the microphone always asked for and
 * what Chrome's on-device model ships first. Swedish is Sweden's. A language
 * with no entry is asked for by its bare code, which every recogniser accepts.
 */
const RECOGNITION: Readonly<Record<string, string>> = {
  en: 'en-US',
  sv: 'sv-SE',
};

export const recognitionTag = (l2: string = learning.l2): string =>
  RECOGNITION[l2] ?? l2;

/**
 * A language's flag as text, for the one place a drawing cannot go: an
 * `<option>` in the grown-ups' picker. The child's picker draws them
 * ([`Flag`](./Flag.tsx)).
 */
const FLAGS: Readonly<Record<string, string>> = {
  en: '🇬🇧',
  sv: '🇸🇪',
};

export const flagFor = (l2: string): string => FLAGS[l2] ?? '🌍';
